import * as React from "react";

import { ChevronDown, ChevronUp } from "lucide-react";

import { TableHead } from "~/components/ui/table";
import { cn } from "~/lib/utils";

export type SortDirection = "asc" | "desc";

export interface SortState {
  /** Empty string means "no active sort" — items keep their incoming order. */
  field: string;
  direction: SortDirection;
}

/**
 * Compares two sortable cell values. Numbers sort numerically, strings with a
 * locale-aware ("es", numeric) comparison, and null/undefined always sort last
 * regardless of direction.
 */
export function compareSortValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  direction: SortDirection
): number {
  const aEmpty = a === null || a === undefined;
  const bEmpty = b === null || b === undefined;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result: number;
  if (typeof a === "number" && typeof b === "number") {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), "es", { numeric: true });
  }
  return direction === "asc" ? result : -result;
}

/**
 * Returns a new copy of `items` sorted by the active field. An empty
 * `sort.field` returns the original array untouched (stable incoming order).
 */
export function sortItems<T>(
  items: T[],
  sort: SortState,
  getValue: (item: T, field: string) => string | number | null | undefined
): T[] {
  if (!sort.field) return items;
  return [...items].sort((a, b) =>
    compareSortValues(
      getValue(a, sort.field),
      getValue(b, sort.field),
      sort.direction
    )
  );
}

/**
 * Sort state + a toggle handler for a single table. Clicking the active column
 * flips direction; clicking a new column starts ascending.
 */
export function useTableSort(
  initial: SortState = { field: "", direction: "asc" }
) {
  const [sort, setSort] = React.useState<SortState>(initial);
  const toggleSort = React.useCallback((field: string) => {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { field, direction: "asc" }
    );
  }, []);
  return { sort, toggleSort, setSort };
}

interface SortableTableHeadProps
  extends React.ComponentPropsWithoutRef<typeof TableHead> {
  sortKey: string;
  sort: SortState;
  onSort: (field: string) => void;
}

/**
 * A `TableHead` that is clickable and sorts on click, showing an up/down
 * chevron for the active column and a faint neutral indicator otherwise.
 * Pass `text-right` via `className` for numeric columns just like a plain head.
 */
export function SortableTableHead({
  sortKey,
  sort,
  onSort,
  className,
  children,
  ...props
}: SortableTableHeadProps) {
  const active = sort.field === sortKey;
  return (
    <TableHead
      {...props}
      onClick={() => onSort(sortKey)}
      aria-sort={
        active
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : "none"
      }
      className={cn(
        "cursor-pointer select-none whitespace-nowrap transition-colors hover:text-foreground",
        active && "text-foreground",
        className
      )}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <SortIndicator active={active} direction={sort.direction} />
      </span>
    </TableHead>
  );
}

function SortIndicator({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection;
}) {
  if (active) {
    return direction === "asc" ? (
      <ChevronUp className="h-3.5 w-3.5 shrink-0" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
    );
  }
  return (
    <span className="inline-flex flex-col opacity-40">
      <ChevronUp className="-mb-1 h-3 w-3" />
      <ChevronDown className="h-3 w-3" />
    </span>
  );
}
